// @deprecated 2026-07-10 — legacy polling chat, superseded by react/ai/EdenChat.jsx (Eden AI system). Kept for AI_SYSTEM=legacy rollback; do not extend.
// NOTE: Requires: npm install markdown-it dompurify  (markdown-it-gfm removed; core already has tables & strikethrough)
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import API_ENDPOINTS from '../ApiEndPoints';
import './AiChat.css';

const AiChat = ({ agentId: propAgentId, initialConversationId = null, ...otherProps }) => {
    // Extract agentId from props - handle both direct prop and Laravel controller variable
    const agentId = propAgentId || otherProps.aiAgent || otherProps.agent;

    // State management
    const [agent, setAgent] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [currentConversationId, setCurrentConversationId] = useState(initialConversationId);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [conversationDeleteId, setConversationDeleteId] = useState(null);
    const [renameConversationId, setRenameConversationId] = useState(null);
    const [conversationTitles, setConversationTitles] = useState({});
    const [leftColumnOpen, setLeftColumnOpen] = useState(false);
    const [debugInfo, setDebugInfo] = useState('');
    const [expandedToolMessages, setExpandedToolMessages] = useState({});

    const conversationTitleInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const pollingRefs = useRef({}); // track active polling intervals keyed by placeholderId
    const messagesRef = useRef(messages);

    // Load agent data and conversations on mount
    useEffect(() => {
        // console.log('AiChat component mounted with props:', { propAgentId, initialConversationId, otherProps });
        // console.log('Resolved agentId:', agentId);
        setDebugInfo(`Component mounted with agentId: ${agentId} (from props: ${JSON.stringify({ propAgentId, otherProps })})`);

        if (agentId) {
            // console.log('Calling loadAgentData...');
            setDebugInfo(prev => prev + '\nCalling loadAgentData...');
            loadAgentData();
        } else {
            // console.log('No agentId provided in props');
            setDebugInfo(prev => prev + '\nNo agentId provided in props');
        }
    }, [agentId]);

    // Load messages when conversation changes
    useEffect(() => {
        if (currentConversationId) {
            loadMessages(currentConversationId);
        } else {
            setMessages([]);
        }
    }, [currentConversationId]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        setExpandedToolMessages(prev => {
            let changed = false;
            const next = { ...prev };
            messages.forEach(msg => {
                if (!msg || msg.message_type !== 'tool_result' || !msg.id) {
                    return;
                }
                if (!(msg.id in next)) {
                    next[msg.id] = false;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [messages]);

    // Focus input when renaming conversation
    useEffect(() => {
        if (renameConversationId && conversationTitleInputRef.current) {
            conversationTitleInputRef.current.focus();
        }
    }, [renameConversationId]);

    // Cleanup polling intervals on unmount
    useEffect(() => {
        return () => {
            Object.values(pollingRefs.current).forEach(clearInterval);
            pollingRefs.current = {};
        };
    }, []);

    // Cancel polling when conversation changes
    useEffect(() => {
        Object.values(pollingRefs.current).forEach(clearInterval);
        pollingRefs.current = {};
    }, [currentConversationId]);

    const loadAgentData = async () => {
        try {
            // console.log('loadAgentData started for agentId:', agentId);
            setDebugInfo(prev => prev + `\nloadAgentData started for agentId: ${agentId}`);

            const url = API_ENDPOINTS.aiChat.getAgent(agentId);
            // console.log('Making API call to:', url);
            setDebugInfo(prev => prev + `\nMaking API call to: ${url}`);

            // Add CSRF token for Laravel API calls
            const response = await axios.get(url, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json',
                }
            });

            // console.log('API response received:', response);
            setDebugInfo(prev => prev + `\nAPI response status: ${response.status}`);

            const { agent, conversations } = response.data;
            // console.log('Agent data:', agent);
            // console.log('Conversations data:', conversations);
            setDebugInfo(prev => prev + `\nAgent loaded: ${agent?.name || 'unnamed'}, Conversations: ${conversations?.length || 0}`);

            setAgent(agent);
            setConversations(conversations);

            // Initialize conversation titles
            const titles = {};
            conversations.forEach(conv => {
                titles[conv.id] = conv.title || '';
            });
            setConversationTitles(titles);

            // console.log('Agent data loaded successfully');
            setDebugInfo(prev => prev + '\nAgent data loaded successfully');

        } catch (error) {
            console.error('Error loading agent data:', error);
            console.error('Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                config: error.config
            });

            const errorMsg = `Error loading agent data: ${error.message}`;
            setStatusMessage(errorMsg);
            setDebugInfo(prev => prev + `\n${errorMsg}`);

            if (error.response) {
                setDebugInfo(prev => prev + `\nResponse status: ${error.response.status}`);
                setDebugInfo(prev => prev + `\nResponse data: ${JSON.stringify(error.response.data)}`);
            }

            // Add more debugging for network errors
            if (error.code === 'NETWORK_ERROR') {
                setDebugInfo(prev => prev + '\nNetwork error - check if API routes are registered');
            }
        }
    };

    const loadMessages = async (conversationId) => {
        try {
            const response = await axios.get(API_ENDPOINTS.aiChat.getMessages(agentId, conversationId));
            setMessages(response.data.messages);
        } catch (error) {
            console.error('Error loading messages:', error);
            setStatusMessage('Error loading messages');
        }
    };

    const createNewConversation = async (title = '') => {
        try {
            const response = await axios.post(API_ENDPOINTS.aiChat.createConversation(agentId), { title });
            const newConversation = response.data.conversation;

            setConversations(prev => [newConversation, ...prev]);
            setConversationTitles(prev => ({ ...prev, [newConversation.id]: newConversation.title || '' }));
            setCurrentConversationId(newConversation.id);

            return newConversation;
        } catch (error) {
            console.error('Error creating conversation:', error);
            setStatusMessage('Error creating conversation');
        }
    };

    const selectConversation = (conversationId) => {
        setCurrentConversationId(conversationId);
    };

    const renameConversation = async (conversationId) => {
        const title = conversationTitles[conversationId];
        if (!title) return;

        try {
            await axios.put(API_ENDPOINTS.aiChat.renameConversation(agentId, conversationId), { title });

            setConversations(prev =>
                prev.map(conv =>
                    conv.id === conversationId ? { ...conv, title } : conv
                )
            );
            setRenameConversationId(null);
        } catch (error) {
            console.error('Error renaming conversation:', error);
            setStatusMessage('Error renaming conversation');
        }
    };

    const deleteConversation = async (conversationId) => {
        try {
            await axios.delete(API_ENDPOINTS.aiChat.deleteConversation(agentId, conversationId));

            setConversations(prev => prev.filter(conv => conv.id !== conversationId));

            if (currentConversationId === conversationId) {
                setCurrentConversationId(null);
                setMessages([]);
            }

            setConversationDeleteId(null);
        } catch (error) {
            console.error('Error deleting conversation:', error);
            setStatusMessage('Error deleting conversation');
        }
    };

    const pollForAssistantResponse = (conversationId, placeholderId) => {
        const start = Date.now();
        const intervalId = setInterval(async () => {
            const placeholderActive = messagesRef.current.find(
                (m) => m.id === placeholderId && m.pending
            );
            if (!placeholderActive) {
                clearInterval(intervalId);
                delete pollingRefs.current[placeholderId];
                return;
            }
            try {
                const resp = await axios.get(
                    API_ENDPOINTS.aiChat.getMessages(agentId, conversationId)
                );
                const fetched = resp.data.messages || [];
                const hasAssistant = fetched.some(
                    (m) => m.role === 'assistant' && m.message_type !== 'tool_result'
                );

                setMessages(prev => {
                    const placeholderMsg = prev.find(
                        (m) => m.id === placeholderId && m.pending
                    );
                    const merged = [...fetched];
                    if (placeholderMsg && !hasAssistant) {
                        merged.push(placeholderMsg);
                    }
                    return merged;
                });
                setTimeout(scrollToBottom, 0);

                if (hasAssistant) {
                    clearInterval(intervalId);
                    delete pollingRefs.current[placeholderId];
                } else if (Date.now() - start > 60000) {
                    clearInterval(intervalId);
                    setMessages(prev => prev.map(m => (
                        m.id === placeholderId
                            ? {
                                ...m,
                                content: 'No response received yet. You may ask another question.',
                                pending: false
                            }
                            : m
                    )));
                    delete pollingRefs.current[placeholderId];
                }
            } catch (error) {
                // keep polling silently
            }
        }, 1500);
        pollingRefs.current[placeholderId] = intervalId;
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || loading) return;
        setLoading(true);
        const messageText = newMessage;
        setNewMessage('');

        // Ensure we have a conversation (optimistic)
        let conversationId = currentConversationId;
        if (!conversationId) {
            const tempConversation = await createNewConversation(messageText.substring(0, 32));
            conversationId = tempConversation?.id;
        }

        if (!conversationId) {
            setStatusMessage('Failed to create conversation');
            setLoading(false);
            return;
        }

        const sentAt = new Date(); // timestamp for matching assistant reply
        const userTempId = 'user-temp-' + Date.now();
        const placeholderId = 'assistant-pending-' + Date.now();

        // Optimistically add user message + pending assistant placeholder
        setMessages(prev => [
            ...prev,
            {
                id: userTempId,
                role: 'user',
                content: messageText,
                created_at: sentAt.toISOString(),
                optimistic: true
            },
            {
                id: placeholderId,
                role: 'assistant',
                content: 'Thinking...',
                created_at: new Date().toISOString(),
                pending: true
            }
        ]);
        scrollToBottom();

        try {
            const response = await axios.post(API_ENDPOINTS.aiChat.sendMessage(agentId), {
                message: messageText,
                conversation_id: conversationId
            });

            // Update current conversation if it was just created
            if (!currentConversationId) {
                setCurrentConversationId(conversationId);
            }

            // Start polling for assistant reply
            pollForAssistantResponse(conversationId, placeholderId);

            // Fallback: one-shot refresh after short delay (handles synchronous server replies)
            setTimeout(() => {
                axios.get(API_ENDPOINTS.aiChat.getMessages(agentId, conversationId))
                    .then(r => {
                        const fetched = r.data.messages || [];
                        const hasAssistant = fetched.some(
                            m => m.role === 'assistant' && m.message_type !== 'tool_result'
                        );
                        setMessages(prev => {
                            const placeholderMsg = prev.find(
                                m => m.id === placeholderId && m.pending
                            );
                            const merged = [...fetched];
                            if (placeholderMsg && !hasAssistant) {
                                merged.push(placeholderMsg);
                            }
                            return merged;
                        });
                        setTimeout(scrollToBottom, 0);
                        if (hasAssistant && pollingRefs.current[placeholderId]) {
                            clearInterval(pollingRefs.current[placeholderId]);
                            delete pollingRefs.current[placeholderId];
                        }
                    })
                    .catch(()=>{ /* ignore */ });
            }, 1200);

            setStatusMessage('Message sent.');
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (error) {
            // Revert optimistic messages on failure
            setMessages(prev => prev.filter(m => m.id !== userTempId && m.id !== placeholderId));
            setStatusMessage('Error sending message');
            setNewMessage(messageText); // restore text
        } finally {
            setLoading(false);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleKeyDown = (e, conversationId) => {
        if (e.key === 'Escape') {
            setRenameConversationId(null);
        } else if (e.key === 'Enter') {
            renameConversation(conversationId);
        }
    };

    const handleTextareaKeyDown = (e) => {
        if (e.key === 'Enter' && !e.ctrlKey) {
            // Enter without Ctrl - submit the form
            e.preventDefault();
            if (!loading && newMessage.trim()) {
                sendMessage(e);
            }
        }
        // Ctrl+Enter allows new line (default behavior)
    };

    const updateConversationTitle = (conversationId, title) => {
        setConversationTitles(prev => ({ ...prev, [conversationId]: title }));
    };

    const renderToolResult = (message) => {
        const result = message.tool_result || {};
        const metadata = result.metadata || {};
        const status = message.status
            || (result.success === true ? 'success' : (result.success === false ? 'error' : null));
        const statusLabel = status === 'success'
            ? 'Success'
            : (status === 'error' ? 'Error' : 'Pending');
        const isExpanded = expandedToolMessages[message.id] ?? false;

        const prettyData = result.data ? JSON.stringify(result.data, null, 2) : null;
        const prettyMeta = metadata && Object.keys(metadata).length
            ? JSON.stringify(metadata, null, 2)
            : null;

        const toggle = () => {
            setExpandedToolMessages(prev => ({
                ...prev,
                [message.id]: !isExpanded,
            }));
        };

        return (
            <div className="tool-result-card">
                <div className="tool-result-header">
                    <div className="tool-result-title">
                        {message.tool_summary || metadata.tool_name || 'Tool Result'}
                    </div>
                    <div className="tool-result-actions">
                        <span className={`tool-result-status ${status || 'pending'}`}>
                            {statusLabel}
                        </span>
                        <button
                            type="button"
                            className="tool-result-toggle"
                            onClick={toggle}
                        >
                            {isExpanded ? 'Hide details' : 'Show details'}
                        </button>
                    </div>
                </div>
                {isExpanded && (
                    <div className="tool-result-body">
                        {result.error && (
                            <div className="tool-result-error">{result.error}</div>
                        )}
                        {result.tool_call_id && (
                            <div className="tool-result-meta">
                                <span className="label">Tool Call ID:</span> {result.tool_call_id}
                            </div>
                        )}
                        {typeof result.execution_time_ms === 'number' && (
                            <div className="tool-result-meta">
                                <span className="label">Duration:</span> {result.execution_time_ms} ms
                            </div>
                        )}
                        {prettyData && (
                            <div className="tool-result-section">
                                <div className="tool-result-section-title">Data</div>
                                <pre>{prettyData}</pre>
                            </div>
                        )}
                        {prettyMeta && (
                            <div className="tool-result-section">
                                <div className="tool-result-section-title">Metadata</div>
                                <pre>{prettyMeta}</pre>
                            </div>
                        )}
                        {!prettyData && !prettyMeta && !result.error && (
                            <pre>{JSON.stringify(result, null, 2)}</pre>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const md = useMemo(() => {
        // Core markdown-it includes tables, strikethrough, autolinks; breaks=true for soft line breaks like chat
        return new MarkdownIt({
            html: false,
            linkify: true,
            breaks: true,
            typographer: false
        });
        // For task lists later:
        //   npm install markdown-it-task-lists
        //   import taskLists from 'markdown-it-task-lists';
        //   return new MarkdownIt({...}).use(taskLists);
    }, []);

    if (!agent) {
        return (
            <div className="text-center">
                <div>Loading...</div>
                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', fontSize: '12px' }}>
                    <strong>Debug Info:</strong>
                    <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>{debugInfo}</pre>
                    <div style={{ marginTop: '10px' }}>
                        <strong>All Props:</strong>
                        <div>propAgentId: {JSON.stringify(propAgentId)}</div>
                        <div>initialConversationId: {JSON.stringify(initialConversationId)}</div>
                        <div>otherProps: {JSON.stringify(otherProps)}</div>
                        <div>resolved agentId: {JSON.stringify(agentId)}</div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <strong>API Endpoint:</strong>
                        <div>{agentId ? API_ENDPOINTS.aiChat.getAgent(agentId) : 'No agentId'}</div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <strong>Axios Default Headers:</strong>
                        <div>{JSON.stringify(axios.defaults.headers.common)}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="aichatbox">
            <div className="hamburger-icon" onClick={() => setLeftColumnOpen(!leftColumnOpen)}>
                <i className="fas fa-bars"></i>
            </div>

            <div className="chat-container">
                <div className={`sidebar-column ${leftColumnOpen ? 'open' : ''}`}>
                    <button
                        type="button"
                        className="new-conversation-btn"
                        onClick={() => createNewConversation()}
                        title="Start new conversation"
                    >
                        <i className="fas fa-plus"></i>
                        New Chat
                    </button>

                    {conversations.length > 0 ? (
                        <ul className="list-group">
                            {conversations.map(conversation => (
                                <li
                                    key={conversation.id}
                                    className={`list-group-item ${conversation.id === currentConversationId ? 'active' : ''}`}
                                    onClick={() => renameConversationId !== conversation.id && selectConversation(conversation.id)}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="flex-grow-1">
                                            {renameConversationId !== conversation.id ? (
                                                <div className="conversation-title">
                                                    {conversation.title || 'Untitled'}
                                                </div>
                                            ) : (
                                                <input
                                                    ref={conversationTitleInputRef}
                                                    type="text"
                                                    className="form-control rename-input"
                                                    value={conversationTitles[conversation.id] || ''}
                                                    onChange={(e) => updateConversationTitle(conversation.id, e.target.value)}
                                                    onKeyDown={(e) => handleKeyDown(e, conversation.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    autoFocus
                                                />
                                            )}
                                        </div>

                                        <div className="conversation-actions">
                                            {conversationDeleteId !== conversation.id ? (
                                                <>
                                                    {renameConversationId !== conversation.id ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn action-btn action-btn-edit"
                                                                onClick={(e) => { e.stopPropagation(); setRenameConversationId(conversation.id); }}
                                                                title="Rename conversation"
                                                            >
                                                                <i className="fa fa-pencil"></i>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn action-btn action-btn-delete"
                                                                onClick={(e) => { e.stopPropagation(); setConversationDeleteId(conversation.id); }}
                                                                title="Delete conversation"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn action-btn action-btn-confirm"
                                                            onClick={(e) => { e.stopPropagation(); renameConversation(conversation.id); }}
                                                            title="Save conversation name"
                                                        >
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn action-btn action-btn-cancel"
                                                        onClick={(e) => { e.stopPropagation(); setConversationDeleteId(null); }}
                                                        title="Cancel deletion"
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn action-btn action-btn-confirm"
                                                        onClick={(e) => { e.stopPropagation(); deleteConversation(conversation.id); }}
                                                        title="Confirm deletion"
                                                    >
                                                        <i className="fas fa-check"></i>
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="no-conversations">
                            No conversations yet
                        </div>
                    )}
                </div>

                <div className="content-column">
                    <div className="card chat-card">
                        <div className="card-header">Chat with {agent.name}</div>
                        <div className="card-body chat-card-body">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="messages-container">
                                        {messages.length > 0 ? (
                                            messages.map(message => {
                                                const isUser = message.role === 'user';
                                                const isToolResult = message.message_type === 'tool_result';
                                                const messageClasses = `message-item ${isUser ? 'sent' : 'received'}${isToolResult ? ' tool-result' : ''}`;
                                                const avatarSrc = isUser
                                                    ? (message.user && message.user.avatar) || '/images/blank-user.png'
                                                    : (message.user && message.user.avatar) || agent.photoUrl || '/images/blank-user.png';
                                                const senderName = isUser ? (message.user?.name || 'You') : (message.user?.name || agent.name);

                                                return (
                                                    <div key={message.id} className={messageClasses}>
                                                        <div className="message-header">
                                                            <div className="message-avatar">
                                                                <img
                                                                    src={avatarSrc}
                                                                    alt={isUser ? 'User' : 'Agent'}
                                                                />
                                                            </div>
                                                            <div className="message-sender">
                                                                {senderName}
                                                            </div>
                                                            <div className="message-time">
                                                                {message.created_at}
                                                            </div>
                                                        </div>
                                                        {message.pending ? (
                                                            <div className="prose message-text">
                                                                <div className="d-flex align-items-center">
                                                                    <span className="mr-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                    {message.content || 'Thinking...'}
                                                                </div>
                                                            </div>
                                                        ) : isToolResult ? (
                                                            renderToolResult(message)
                                                        ) : (
                                                            <div className="prose message-text">
                                                                {isUser ? (
                                                                    message.content
                                                                ) : (
                                                                    <div
                                                                        className="ai-markdown"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: DOMPurify.sanitize(message.content || '')
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="alert alert-info">
                                                No messages yet.
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </div>
                            </div>

                            <div className="row message-input-container">
                                <div className="col-md-12">
                                    <form onSubmit={sendMessage}>
                                        <div className="input-group">
                                            <textarea
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                onKeyDown={handleTextareaKeyDown}
                                                className="form-control"
                                                placeholder="Type your message... (Press Enter to send, Ctrl+Enter for new line)"
                                                disabled={loading}
                                            />
                                            <span className="input-group-append">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-sm"
                                                    disabled={loading || !newMessage.trim()}
                                                >
                                                    {loading && (
                                                        <span className="mr-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    )}
                                                    Send
                                                </button>
                                            </span>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {statusMessage && (
                                <div className="row" id="status-message">
                                    <div className="col-md-12">
                                        <div className="alert alert-dismissible">
                                            {statusMessage}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AiChat;
